'use client';

import React, { createContext, ReactNode, useContext } from 'react';

import { useAuth } from '@/providers/AuthProvider';
import { useChannels } from '@/providers/ChannelsProvider';
import { useChat } from '@/providers/ChatContext';
import { ChannelActionsContextProps } from '@/types/chat';
import { Side } from '@/types/user';
import { handleBlockStatus, handleLeftStatus } from '@/utils/helpers/chat';

const ChannelActionsContext = createContext<
  ChannelActionsContextProps | undefined
>(undefined);

export const ChannelActionsProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const { setChannels } = useChat();
  const { updateChannelById } = useChannels();
  const { user } = useAuth();

  const updateChatBlockedStatus = (
    selfStatus: boolean,
    peerStatus: boolean,
    performedByUserId: string,
    affectedUserId: string
  ) => {
    const targetId =
      performedByUserId === user?.id ? affectedUserId : performedByUserId;

    setChannels(prev =>
      handleBlockStatus(prev, selfStatus, peerStatus, targetId)
    );
  };

  const updateChatLeftStatus = (chatId: string, side: Side, leftAt: string) => {
    setChannels(prev => handleLeftStatus(prev, chatId, side, leftAt));
  };

  const updateUnreadCount = (chatId: string, unreadCount: number) => {
    updateChannelById(chatId, channel => ({
      ...channel,
      unreadCount
    }));
  };

  return (
    <ChannelActionsContext.Provider
      value={{
        updateUnreadCount,
        updateChatLeftStatus,
        updateChatBlockedStatus
      }}
    >
      {children}
    </ChannelActionsContext.Provider>
  );
};

export const useChannelActions = () => {
  const context = useContext(ChannelActionsContext);

  if (!context) {
    throw new Error(
      'useChannelActions must be used within a ChannelActionsProvider'
    );
  }

  return context;
};
